import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Cohort, Level, Profile, Unit } from '@/types'
import {
  ArrowLeft,
  Edit,
  Users,
  Calendar,
  User,
  Layers,
  Lock,
  Unlock,
  BookOpen,
  Mail
} from 'lucide-react'

interface CohortWithRelations extends Cohort {
  level: Level | null
  teacher: Profile | null
}

export default function CohortDetail() {
  const { cohortId } = useParams()
  const [cohort, setCohort] = useState<CohortWithRelations | null>(null)
  const [units, setUnits] = useState<Unit[]>([])
  const [students, setStudents] = useState<Profile[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadCohort()
  }, [cohortId])

  const loadCohort = async () => {
    try {
      // Load cohort
      const { data: cohortData, error: cohortError } = await (supabase
        .from('cohorts') as any)
        .select('*')
        .eq('id', cohortId as string)
        .single()

      if (cohortError) throw cohortError

      const cohort = cohortData as Cohort

      // Load level
      const { data: levelData } = await (supabase
        .from('levels') as any)
        .select('*')
        .eq('id', cohort.level_id)
        .single()

      // Load teacher
      const { data: teacherData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', cohort.teacher_id)
        .single()

      // Load units for this level
      const { data: unitsData } = await supabase
        .from('units')
        .select('*')
        .eq('level_id', cohort.level_id)
        .order('order_num')

      // Load students
      if (cohort.student_ids && cohort.student_ids.length > 0) {
        const { data: studentsData } = await supabase
          .from('profiles')
          .select('*')
          .in('id', cohort.student_ids)

        setStudents((studentsData as unknown as Profile[]) || [])
      }

      setCohort({
        ...cohort,
        level: levelData as Level || null,
        teacher: teacherData as unknown as Profile || null
      } as CohortWithRelations)

      setUnits((unitsData as unknown as Unit[]) || [])
    } catch (error) {
      console.error('Error loading cohort:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0055A4]" />
      </div>
    )
  }

  if (!cohort) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Cohorte no encontrada</p>
        <Link to="/admin/cohorts" className="text-[#0055A4] hover:underline">
          Volver a la lista
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/cohorts"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{cohort.name}</h1>
            <p className="text-gray-600">{cohort.description}</p>
          </div>
        </div>
        <Link
          to={`/admin/cohorts/${cohortId}/edit`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] transition-colors"
        >
          <Edit className="w-5 h-5" />
          Editar
        </Link>
      </div>

      {/* Info Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Level */}
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Layers className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Nivel</h3>
          </div>
          <p className="text-lg text-gray-800">{cohort.level?.name || 'Sin asignar'}</p>
          <p className="text-sm text-gray-500">Nivel {cohort.level?.order_num}</p>
        </div>

        {/* Teacher */}
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-green-100">
              <User className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Profesor</h3>
          </div>
          <p className="text-lg text-gray-800">
            {cohort.teacher ? `${cohort.teacher.first_name} ${cohort.teacher.last_name}` : 'Sin asignar'}
          </p>
          <p className="text-sm text-gray-500">{cohort.teacher?.email}</p>
        </div>

        {/* Dates */}
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-[#0055A4]/10">
              <Calendar className="w-5 h-5 text-[#0055A4]" />
            </div>
            <h3 className="font-semibold text-gray-900">Fechas</h3>
          </div>
          <p className="text-gray-800">Inicio: {formatDate(cohort.start_date)}</p>
          <p className="text-sm text-gray-500">
            {cohort.end_date ? `Fin: ${formatDate(cohort.end_date)}` : 'Sin fecha de fin'}
          </p>
          {(cohort as any).schedule_day && (
            <p className="text-gray-800 mt-1">
              Horario: {(cohort as any).schedule_day} de {(cohort as any).schedule_start_time} a {(cohort as any).schedule_end_time}
            </p>
          )}
        </div>
      </div>

      {/* Units Progress */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-purple-100">
            <BookOpen className="w-5 h-5 text-purple-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Unidades del Nivel ({cohort.unlocked_unit_index + 1} de {units.length} desbloqueadas)
          </h2>
        </div>

        <div className="space-y-2">
          {units.map((unit, index) => {
            const isUnlocked = index <= cohort.unlocked_unit_index
            return (
              <div
                key={unit.id}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  isUnlocked ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className={`p-2 rounded-lg ${isUnlocked ? 'bg-green-100' : 'bg-gray-100'}`}>
                  {isUnlocked ? (
                    <Unlock className="w-4 h-4 text-green-600" />
                  ) : (
                    <Lock className="w-4 h-4 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${isUnlocked ? 'text-gray-900' : 'text-gray-500'}`}>
                    Unidad {unit.order_num}: {unit.title}
                  </p>
                  <p className="text-xs text-gray-500">{unit.description}</p>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  isUnlocked ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {isUnlocked ? 'Desbloqueada' : 'Bloqueada'}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Students */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-orange-100">
            <Users className="w-5 h-5 text-orange-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Estudiantes ({students.length}/{cohort.max_students})
          </h2>
        </div>

        {students.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-3">
            {students.map((student, index) => (
              <div
                key={student.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600 font-medium text-sm">
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {student.first_name} {student.last_name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{student.email}</p>
                </div>
                <a
                  href={`mailto:${student.email}`}
                  className="p-2 text-gray-400 hover:text-[#0055A4] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg">
            No hay estudiantes asignados a esta cohorte
          </div>
        )}
      </div>
    </div>
  )
}
